import { useEffect, useState } from 'react';

/**
 * Reactively track whether the viewport matches a CSS media query.
 * Useful for swapping JS-driven UI patterns (split-pane → stacked) based
 * on width. Pure CSS should handle visual tweaks; this is for behavior.
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    const mql = window.matchMedia(query);
    setMatches(mql.matches);
    const handler = (e: MediaQueryListEvent) => setMatches(e.matches);
    // Safari < 14 still needs addListener/removeListener
    if (mql.addEventListener) {
      mql.addEventListener('change', handler);
      return () => mql.removeEventListener('change', handler);
    }
    mql.addListener(handler);
    return () => mql.removeListener(handler);
  }, [query]);

  return matches;
}

/* Los dos anchos a los que la interfaz cambia de FORMA, no solo de estilo.
 *
 * Viven aquí, con nombre, porque el mismo número se usa en dos sitios: React
 * pone un atributo (`data-narrow`, `data-compact`) y el CSS trae un único
 * juego de reglas colgando de ese atributo. La alternativa —un `@media` en el
 * CSS y el número repetido en el TSX— es exactamente cómo las dos apariencias
 * se separan: alguien arregla una y no la otra. Ya pasó con el raíl.
 *
 * Son números DISTINTOS a propósito: el raíl ancho estorba antes que la tabla.
 */

/** El raíl se colapsa solo por debajo de esto, decida lo que decida el botón. */
export const RAIL_NARROW = '(max-width: 1100px)';

/** La bandeja pasa a filas apiladas por debajo de esto — el mismo modo que usa
 *  cuando hay un ticket abierto y el panel se estrecha. */
export const LIST_NARROW = '(max-width: 900px)';

/** Viewport is narrow enough that outlook split-pane should collapse to
 * stacked (list OR detail). Also used to auto-collapse the sidebar.
 *
 * Se conserva como alias de `LIST_NARROW`, que es su mismo ancho: no tenía
 * ningún llamante cuando el paso 36 añadió las constantes, y dejarlo con su
 * propio `'(max-width: 900px)'` escrito al lado habría creado justo la
 * duplicación que estas constantes existen para evitar. */
export function useIsNarrow(): boolean {
  return useMediaQuery(LIST_NARROW);
}
